import { useCallback, useMemo, useState } from 'react';
import { ApiOutlined, NodeIndexOutlined } from '@ant-design/icons';

import { DataTable, Tile } from '../../../../components/shared';
import { Badge, Button, Empty, Skeleton } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { KHOI_BANG, LE_BANG } from '../../chung/bang';
import { KhoiLoi } from '../../chung/components/KhoiLoi';
import { LOC_AI_MAC_DINH, nhanNguoi, thamSoLocLuot, type LocAi } from '../../chung/nghiep-vu/loc-ai';
import { TRONG, dayDu, gio } from '../../chung/nghiep-vu/thoi-gian';
import type { LuotGoi, SapXep } from '../../chung/nghiep-vu/types-vet';
import { useDanhSachLuotGoi, useNhanAi } from '../hooks';
import { nhanTenant, useNguonVet } from '../nguon';
import { gopLuotGoi, khoangMs } from '../nghiep-vu/vet';
import { BocTachToken } from './BocTachToken';
import { cotFox, cotTokenThat, LOP_O_SO } from './cot-fox';
import { ChanDanhSach } from './ChanDanhSach';
import { ChonSapXep } from './ChonSapXep';
import { ChuoiBuoc } from './ChuoiBuoc';
import { GiaVon, NhanCanhBao, NhanTrangThaiGiaVon, NhanUocTinh, TietKiemDem } from './NhanTinCay';

/**
 * Từng lượt gọi mô hình — một dòng mỗi lần gọi. Port `vet/BangLuotGoi.tsx` native.
 *
 * Cổng tenant: token Fox đầu vào, đầu ra, tổng (ADR-0123 mục 5). Cổng nền tảng thêm token thật, giá vốn và bóc tách bảy
 * loại token ở **hàng mở rộng**: bảy cột số cạnh nhau thì cả bảng thành một ma trận không ai đọc theo hàng.
 *
 * `trace_id` là nút: từ một lượt gọi tốn bất thường, việc kế tiếp luôn là xem nó nằm trong chuỗi nào.
 */
export function BangLuotGoi({ tu, den, loc = LOC_AI_MAC_DINH }: { tu: string; den: string; loc?: LocAi }) {
  const { t, locale } = useT();
  const { cotTenant, tenTenant, nenTang } = useNguonVet();
  const nhanAi = useNhanAi();
  const [sapXep, setSapXep] = useState<SapXep>('moi_nhat');
  const [dangXem, setDangXem] = useState<{ traceId: string; tenantId?: string } | null>(null);

  const locLuot = useMemo(() => thamSoLocLuot(loc), [loc]);
  const thamSo = useMemo(() => ({ tu, den, sap_xep: sapXep, ...locLuot }), [tu, den, sapXep, locLuot]);
  const coLoc = Object.keys(locLuot).length > 0;
  const truyVan = useDanhSachLuotGoi(thamSo);
  const dong = useMemo(() => gopLuotGoi(truyVan.data?.pages), [truyVan.data]);
  const coGiaVon = dong.some((r) => r.chi_phi_von !== undefined);

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = truyVan;
  const taiThem = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="space-y-4">
      <Tile className={KHOI_BANG}>
        <div className="border-b border-default">
          <ChonSapXep giaTri={sapXep} onDoi={setSapXep} />
        </div>
        {truyVan.isPending ? (
          <Skeleton className="m-4 h-72" />
        ) : truyVan.isError ? (
          <KhoiLoi loi={truyVan.error} onThuLai={() => void truyVan.refetch()} />
        ) : (
          <>
            <DataTable<LuotGoi>
              caption={t('nktvSuDungAi.luotGoi.bang')}
              className={LE_BANG}
              rowKey="event_id"
              size="small"
              dataSource={dong}
              pagination={false}
              expandable={
                nenTang
                  ? {
                      expandedRowRender: (r) => (
                        <BocTachToken token={r.token} tongToken={r.tong_token ?? null} className="py-1" />
                      ),
                      expandRowByClick: true,
                    }
                  : undefined
              }
              locale={{
                emptyText: coLoc ? (
                  <Empty icon={ApiOutlined} title={t('nktvSuDungAi.danhSach.rongLoc')} />
                ) : (
                  <Empty
                    icon={ApiOutlined}
                    title={t('nktvSuDungAi.luotGoi.rongTieuDe')}
                    description={t('nktvSuDungAi.luotGoi.rongMoTa')}
                  />
                ),
              }}
              columns={[
                {
                  title: t('nktvSuDungAi.luotGoi.cotThoiGian'),
                  width: 84,
                  render: (_v, r) => (
                    <time
                      dateTime={r.thoi_gian}
                      title={dayDu(r.thoi_gian, locale)}
                      className="font-mono text-xs text-content-secondary"
                    >
                      {gio(r.thoi_gian, locale)}
                    </time>
                  ),
                },
                {
                  title: t('nktvSuDungAi.luotGoi.cotMoHinh'),
                  width: 170,
                  render: (_v, r) => (
                    <div className="min-w-0 max-w-[10rem]">
                      <div className="truncate text-sm text-content-primary" title={r.mo_hinh ?? undefined}>
                        {r.mo_hinh ?? TRONG}
                      </div>
                      {/* Cổng tenant không có cột độ tin cậy: lượt thất bại báo ngay dưới tên mô hình. */}
                      {!nenTang && r.ket_qua === 'that_bai' ? (
                        <Badge variant="danger">{t('nktv.ketQua.that_bai')}</Badge>
                      ) : (
                        <div className="truncate text-xs text-content-muted">
                          {(nenTang
                            ? [r.nha_cung_cap && nhanAi.nhaCungCap(r.nha_cung_cap), r.kieu_api]
                            : [r.nha_cung_cap && nhanAi.nhaCungCap(r.nha_cung_cap)]
                          )
                            .filter(Boolean)
                            .join(' · ') || TRONG}
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  title: t('nktvSuDungAi.luotGoi.cotTinhNang'),
                  width: 130,
                  render: (_v, r) => {
                    const tinhNang = nhanAi.tinhNang(r.tinh_nang, r.nhan_tinh_nang) || TRONG;
                    return (
                      <div className="min-w-0 max-w-[7.5rem]">
                        <div className="truncate text-xs text-content-secondary" title={tinhNang}>
                          {tinhNang}
                        </div>
                        <div className="truncate text-xs text-content-muted">{nhanAi.service(r.service)}</div>
                      </div>
                    );
                  },
                },
                ...(cotTenant
                  ? [
                      {
                        title: t('nktvSuDungAi.cotTenant'),
                        width: 160,
                        render: (_v: unknown, r: LuotGoi) => (
                          <span className="block max-w-[10rem] truncate text-sm text-content-secondary">
                            {nhanTenant(tenTenant, r.tenant_id)}
                          </span>
                        ),
                      },
                    ]
                  : []),
                {
                  title: t('nktvSuDungAi.luotGoi.cotNguoi'),
                  width: 140,
                  render: (_v, r) => {
                    const nguoi = nhanNguoi(r.actor_ten, r.nguoi_dung_id, nhanAi);
                    return (
                      <div className="min-w-0 max-w-[8.5rem]">
                        <div
                          className="truncate text-sm text-content-secondary"
                          title={[nguoi.title, r.don_vi_ten].filter(Boolean).join(' · ') || undefined}
                        >
                          {nguoi.nhan}
                        </div>
                        <div className="truncate text-xs text-content-muted">{r.doi_tuong_ten ?? r.doi_tuong_id ?? ''}</div>
                      </div>
                    );
                  },
                },
                cotFox<LuotGoi>(t, locale),
                ...(nenTang ? [cotTokenThat<LuotGoi>(t, locale)] : []),
                ...(coGiaVon
                  ? [
                      {
                        title: t('nktvSuDungAi.cotGiaVon'),
                        width: 150,
                        align: 'right' as const,
                        render: (_v: unknown, r: LuotGoi) => (
                          <NhanTrangThaiGiaVon
                            trangThai={r.trang_thai}
                            thieuTyGia={r.thieu_ty_gia}
                            moHinh={r.mo_hinh}
                            giaTri={<GiaVon giaTri={r.chi_phi_von} donVi={r.don_vi_tien ?? null} />}
                          />
                        ),
                      },
                    ]
                  : []),
                ...(nenTang && dong.some((r) => r.tiet_kiem_dem !== undefined)
                  ? [
                      {
                        title: t('nktvSuDungAi.luotGoi.cotTietKiem'),
                        width: 130,
                        align: 'right' as const,
                        render: (_v: unknown, r: LuotGoi) => (
                          <TietKiemDem giaTri={r.tiet_kiem_dem ?? null} donVi={r.don_vi_tien ?? null} />
                        ),
                      },
                    ]
                  : []),
                {
                  title: t('nktvSuDungAi.luotGoi.cotDoTre'),
                  width: 90,
                  align: 'right',
                  render: (_v, r) => (
                    <span className={`${LOP_O_SO} text-content-secondary`}>{khoangMs(r.do_tre_ms, locale)}</span>
                  ),
                },
                ...(nenTang
                  ? [
                      {
                        title: t('nktvSuDungAi.luotGoi.cotTinCay'),
                        width: 130,
                        render: (_v: unknown, r: LuotGoi) => (
                          <span className="flex flex-wrap items-center gap-1">
                            {r.canh_bao && <NhanCanhBao canhBao={r.canh_bao} />}
                            {r.uoc_tinh === true && <NhanUocTinh />}
                            {r.ket_qua === 'that_bai' && <Badge variant="danger">{t('nktv.ketQua.that_bai')}</Badge>}
                          </span>
                        ),
                      },
                    ]
                  : []),
                {
                  title: t('nktvSuDungAi.luotGoi.cotChuoi'),
                  width: 64,
                  fixed: 'right',
                  render: (_v, r) => {
                    const traceId = r.trace_id;
                    if (traceId === null) return <span className="text-xs text-content-muted">{TRONG}</span>;
                    return (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<NodeIndexOutlined />}
                        aria-label={t('nktvSuDungAi.luotGoi.xemChuoi')}
                        title={t('nktvSuDungAi.luotGoi.xemChuoi')}
                        onClick={(e) => {
                          // Cổng nền tảng: hàng mở rộng bật theo cú bấm vào dòng — không chặn nổi bọt thì bấm nút này
                          // vừa mở ngăn kéo vừa bung hàng bóc tách.
                          e.stopPropagation();
                          setDangXem({ traceId, tenantId: r.tenant_id });
                        }}
                      />
                    );
                  },
                },
              ]}
            />

            <ChanDanhSach
              coTrangTiep={hasNextPage === true}
              dangTai={isFetchingNextPage}
              onTaiThem={taiThem}
              soDong={dong.length}
              ghiChu={sapXep === 'ton_nhat' ? t('nktvSuDungAi.danhSach.xepHangMotTrang') : undefined}
            />
          </>
        )}
      </Tile>

      {dangXem !== null && (
        <ChuoiBuoc traceId={dangXem.traceId} tenantId={dangXem.tenantId} onDong={() => setDangXem(null)} />
      )}
    </div>
  );
}
